/**
 * FS-161(b) — rascunho local do wizard de Nova Operação (anti-perda).
 *
 * Preencher uma operação custa minutos de digitação: NF, sacado com documento,
 * valores, vencimento, assinantes. Antes disto, qualquer saída da tela (F5,
 * clique no menu, aba fechada por engano) apagava tudo. Esta lib guarda o
 * formulário para que ele possa VOLTAR — e é onde mora a regra que, quebrada,
 * vaza PII ou ressuscita um estado velho.
 *
 * Decisões do owner (2026-08-21, P7):
 *
 * - **sessionStorage, NUNCA localStorage.** O rascunho carrega PII (CPF/CNPJ do
 *   sacado, número da NF, valores). sessionStorage morre com a aba; localStorage
 *   deixaria o dado em DISCO por tempo indeterminado, num navegador que pode ser
 *   compartilhado — custo LGPD que a decisão não aceitou. O único storage
 *   persistente do Portal continua sendo o tema.
 * - **TTL de 24h.** Rascunho velho é ruído: os valores de uma operação de ontem
 *   raramente valem hoje, e PII não fica apodrecendo no navegador. Vencido é
 *   descartado NA LEITURA (a chave some).
 * - **Chave por MODO e por USUÁRIO** (`zemo.pcp.draft.newop.<mode>.<userId>`).
 *   Sem usuário identificado NÃO se persiste nada: gravar antes de saber de quem
 *   é o dado é o caminho para um usuário ver o rascunho de outro.
 * - **Versão de schema (v:1).** Formato diferente é DESCARTADO, nunca "melhor
 *   esforço": estado meio-lido num formulário de dinheiro é pior que estado
 *   nenhum.
 * - **Whitelist ESTRITA de campos**, aplicada na escrita E na leitura: só o que
 *   o usuário digitou. Resultado de simulação, erros e listas de servidor
 *   (produtos/clientes/representantes) NUNCA entram — são dados FRESCOS por
 *   definição, e devolvê-los do storage mostraria número de dinheiro velho como
 *   se fosse a simulação corrente.
 * - **Falha de storage é MUDA** (quota, modo privado, storage bloqueado): o
 *   rascunho é conveniência, não pode derrubar a tela nem gritar com o usuário.
 *
 * Money-path: os valores voltam como a STRING MASCARADA que estava no estado do
 * wizard ("R$ 10.000,00"). Nada de guardar centavos ou número aqui —
 * `parseDecimalBR` continua sendo a ÚNICA fronteira de conversão, e uma segunda
 * fronteira seria dupla conversão silenciosa.
 *
 * A RESTAURAÇÃO nunca é silenciosa: quem decide é a tela, por banner explícito
 * (Continuar/Descartar). Repor valores de dinheiro sem o usuário pedir é a
 * variante de UX do erro de ×100.
 */
import { isReceivableTouched } from "./operations";

export const DRAFT_VERSION = 1;
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
export const DRAFT_KEY_PREFIX = "zemo.pcp.draft.newop";
export const DRAFT_DEBOUNCE_MS = 800;

// Campos do wizard que o rascunho carrega. Whitelist, não blacklist: campo novo
// no formulário só passa a ser persistido quando ALGUÉM o adiciona aqui de
// propósito — o default seguro é não gravar.
export const DRAFT_STATE_FIELDS = [
  "productId",
  "cedenteId",
  "taxa",
  "descricao",
  "receivables",
  "selectedSignerIds",
  "step",
];

// Campos de um recebível. `_key` fica DE FORA: é identidade de render (gerada
// no `emptyReceivable`), não dado — persistir traria chaves duplicadas de volta.
export const DRAFT_RECEIVABLE_FIELDS = [
  "external_id",
  "payer_name",
  "payer_document",
  "net_face_value",
  "requested_advance_value",
  "due_date",
  "backing_type",
  "payer_cliente_id",
];

/**
 * FS-162 — marcadores BOOLEANOS do recebível. Lista SEPARADA da de texto de
 * propósito: com um whitelist só, `{_advanceTouched: "sim"}` vindo de um
 * storage adulterado passaria pelo ramo de string e voltaria ao estado do React
 * como uma string truthy — confusão de TIPO num marcador que decide se o valor
 * a antecipar pode ser sobrescrito. Cada campo tem UM tipo aceito.
 *
 * `_advanceTouched` é persistido porque é ele que decide se o valor volta a
 * ACOMPANHAR o valor de face (Q1): sem ele, restaurar um rascunho e depois
 * corrigir o valor de face SOBRESCREVERIA em silêncio um valor digitado à mão.
 * `_pctInput` (o texto do campo % em foco) fica DE FORA das duas listas: o
 * percentual é DERIVADO do valor, e guardar uma segunda representação do mesmo
 * dinheiro é a definição de dupla fonte da verdade.
 */
export const DRAFT_RECEIVABLE_FLAGS = ["_advanceTouched"];

export function draftKey(mode, userId) {
  if (!mode || !userId) return null;
  return `${DRAFT_KEY_PREFIX}.${mode}.${userId}`;
}

// sessionStorage do navegador, resolvido tarde e sob try/catch: em modo privado
// / storage bloqueado o simples ACESSO à propriedade lança.
function defaultStorage() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

const resolveStorage = (storage) => storage || defaultStorage();

const texto = (v) => (typeof v === "string" ? v : "");

function sanitizeReceivable(r) {
  const out = {};
  if (!r || typeof r !== "object") return out;
  for (const campo of DRAFT_RECEIVABLE_FIELDS) {
    if (typeof r[campo] === "string" && r[campo] !== "") out[campo] = r[campo];
  }
  // FS-162: ramo BOOLEANO. `false` é gravado tanto quanto `true` — AUSENTE é um
  // terceiro estado com significado próprio (rascunho do formato anterior, que
  // a tela trata pelo fail-safe de não mexer no dinheiro restaurado).
  for (const campo of DRAFT_RECEIVABLE_FLAGS) {
    if (typeof r[campo] === "boolean") out[campo] = r[campo];
  }
  return out;
}

/**
 * Estado do wizard → objeto persistível. Aplicado nos DOIS sentidos (gravar e
 * ler): na escrita impede que dado de servidor vá ao storage; na leitura impede
 * que storage adulterado (ou de uma versão futura mal-comportada) volte ao
 * estado do React.
 */
export function sanitizeDraftState(state) {
  const s = state && typeof state === "object" ? state : {};
  return {
    productId: texto(s.productId),
    cedenteId: texto(s.cedenteId),
    taxa: texto(s.taxa),
    descricao: texto(s.descricao),
    receivables: Array.isArray(s.receivables) ? s.receivables.map(sanitizeReceivable) : [],
    selectedSignerIds: Array.isArray(s.selectedSignerIds)
      ? s.selectedSignerIds.filter((id) => typeof id === "string" && id !== "")
      : [],
    step: Number.isInteger(s.step) && s.step >= 1 ? s.step : 1,
  };
}

/**
 * O formulário tem CONTEÚDO que valha um rascunho?
 *
 * Usado por `loadDraft` para não oferecer "restaurar" um formulário vazio. NÃO
 * é o predicado de "mexeu" da tela — esse é `isDirtySince`, contra baseline;
 * ver a nota lá sobre o deep-link, que chega preenchido sem ninguém digitar.
 *
 * `cedenteId` e `selectedSignerIds` ficam DE FORA de propósito: no modo cedente
 * o cedente vem do token e os assinantes padrão do cadastro já chegam
 * pré-selecionados — um rascunho feito só disso não tem trabalho humano dentro.
 */
export function isDraftDirty(state) {
  if (!state || typeof state !== "object") return false;
  if (texto(state.productId) || texto(state.taxa) || texto(state.descricao)) return true;
  return Array.isArray(state.receivables) && state.receivables.some(isReceivableTouched);
}

/**
 * Assinatura canônica do formulário — a MESMA projeção que vai ao storage.
 *
 * É por ela que a tela decide "mexeu?": comparar a assinatura corrente com a
 * BASELINE (tirada depois do preenchimento programático inicial) em vez de
 * perguntar "tem algo preenchido?". A diferença importa: com deep-link
 * `?cliente_id=`, o sacado chega preenchido sem ninguém digitar — pela pergunta
 * ingênua a tela nasceria "suja", avisando sobre perda de dados que o usuário
 * nunca produziu e gravando rascunho-fantasma.
 *
 * `_key` fica fora (a sanitização o descarta): é identidade de render e muda a
 * cada `emptyReceivable()`, o que tornaria toda linha nova uma "alteração".
 */
export function draftSignature(state) {
  return JSON.stringify(sanitizeDraftState(state));
}

/**
 * O formulário divergiu da baseline? Baseline ausente = ainda não tirada (load
 * inicial em curso) ⇒ NUNCA sujo: avisar antes de o usuário poder ter feito
 * algo é alarme falso, e não há o que perder.
 *
 * Simétrico de propósito: desfazer as edições e voltar ao estado de referência
 * volta a LIMPO. Um "sujou, sujou para sempre" faria a tela perguntar na saída
 * de um formulário idêntico ao que ela abriu.
 */
export function isDirtySince(baseline, state) {
  if (typeof baseline !== "string") return false;
  return draftSignature(state) !== baseline;
}

/**
 * Produto do rascunho × catálogo carregado AGORA.
 *
 * O rascunho pode ter dormido até 24h e o produto pode ter sido desabilitado
 * nesse meio-tempo. Restaurá-lo mudo deixaria a tela num estado que os gates
 * recusam sem explicar por quê — devolver `unavailable` é o que permite à tela
 * DIZER o que houve em vez de só travar.
 */
export function resolveDraftProduct(products, productId) {
  if (!productId) return { productId: "", unavailable: false };
  const encontrado = (Array.isArray(products) ? products : []).find((p) => p?.id === productId);
  return encontrado?.enabled
    ? { productId, unavailable: false }
    : { productId: "", unavailable: true };
}

/**
 * Cedente do rascunho × lista carregada AGORA — mesma mecânica do produto, e
 * pelo MESMO motivo, só que com um final pior se faltar.
 *
 * O `submit` começa com `if (!product || !cedente || ...) return;`: um
 * `cedenteId` que não casa com nenhum cedente da lista (arquivado, saiu do
 * filtro `?status=active`) não trava nada pelo caminho — o usuário atravessa o
 * wizard inteiro e o "Confirmar e criar operação" vira **no-op MUDO**, sem
 * POST, sem toast, sem navegação. Não restaurar o id órfão faz o gate da etapa
 * Cedente barrar ali, visivelmente, com o motivo na tela.
 *
 * Sem `enabled` aqui: a lista de cedentes já vem filtrada pelo servidor —
 * estar nela É a disponibilidade.
 *
 * ATENÇÃO ao chamador: só revalide com a lista CARREGADA COM SUCESSO. Lista
 * vazia por falha de rede daria "sumiu" para um id perfeitamente válido — ver
 * a nota de `draftUnavailableNotice` e o gate `loadFailedParts` na tela.
 */
export function resolveDraftCedente(cedentes, cedenteId) {
  if (!cedenteId) return { cedenteId: "", unavailable: false };
  const encontrado = (Array.isArray(cedentes) ? cedentes : []).find((c) => c?.id === cedenteId);
  return encontrado ? { cedenteId, unavailable: false } : { cedenteId: "", unavailable: true };
}

/**
 * Frase ÚNICA para o que a restauração descartou por não existir mais.
 *
 * Uma frase montada, não duas coladas: juntar sentenças capitalizadas com " e "
 * produz "…não está mais disponível e O cedente…", que denuncia o `join` no
 * meio do texto. Com os dois faltando, o sujeito é composto e o verbo vai ao
 * plural.
 */
export function draftUnavailableNotice({ produto = false, cedente = false } = {}) {
  if (!produto && !cedente) return null;
  const sujeito = produto && cedente ? "O produto e o cedente" : produto ? "O produto" : "O cedente";
  const verbo = produto && cedente ? "não estão mais disponíveis" : "não está mais disponível";
  return `${sujeito} do rascunho ${verbo} — escolha novamente para continuar.`;
}

/** Grava o rascunho. Devolve `false` (mudo) quando não deu para gravar. */
export function saveDraft(mode, userId, state, storage) {
  const key = draftKey(mode, userId);
  const st = resolveStorage(storage);
  if (!key || !st) return false;
  try {
    st.setItem(
      key,
      JSON.stringify({ v: DRAFT_VERSION, savedAt: Date.now(), state: sanitizeDraftState(state) })
    );
    return true;
  } catch {
    // Quota estourada / storage bloqueado: o rascunho é conveniência.
    return false;
  }
}

/** Apaga o rascunho daquele usuário/modo. Nunca lança. */
export function clearDraft(mode, userId, storage) {
  const key = draftKey(mode, userId);
  const st = resolveStorage(storage);
  if (!key || !st) return;
  try {
    st.removeItem(key);
  } catch {
    /* storage indisponível: nada a fazer */
  }
}

/**
 * Apaga TODOS os rascunhos (logout). O usuário que sai não pode deixar PII para
 * o próximo que logar na mesma aba. Só as chaves do prefixo — o resto do
 * sessionStorage não é nosso.
 */
export function clearAllDrafts(storage) {
  const st = resolveStorage(storage);
  if (!st) return;
  try {
    const alvos = [];
    for (let i = 0; i < st.length; i += 1) {
      const k = st.key(i);
      if (typeof k === "string" && k.startsWith(`${DRAFT_KEY_PREFIX}.`)) alvos.push(k);
    }
    // Coleta ANTES de remover: remover durante a varredura reindexa as chaves.
    alvos.forEach((k) => st.removeItem(k));
  } catch {
    /* storage indisponível: nada a fazer */
  }
}

/**
 * Lê o rascunho utilizável. Devolve `{state, savedAt}` ou `null` — e, em todo
 * caso de descarte (vencido, versão errada, corrompido, vazio), APAGA a chave:
 * um rascunho que não vai ser oferecido não tem por que continuar guardando PII.
 */
export function loadDraft(mode, userId, storage) {
  const key = draftKey(mode, userId);
  const st = resolveStorage(storage);
  if (!key || !st) return null;

  let raw = null;
  try {
    raw = st.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;

  const descartar = () => {
    try {
      st.removeItem(key);
    } catch {
      /* nada a fazer */
    }
    return null;
  };

  let env;
  try {
    env = JSON.parse(raw);
  } catch {
    return descartar();
  }
  if (!env || typeof env !== "object") return descartar();
  // Schema diferente: descarta inteiro. Ler "o que dá" de um formulário de
  // dinheiro é pior que não ler nada.
  if (env.v !== DRAFT_VERSION) return descartar();

  const { savedAt } = env;
  if (!Number.isFinite(savedAt)) return descartar();
  const idade = Date.now() - savedAt;
  // Idade negativa = carimbo no futuro (relógio inconsistente): fail-closed, ou
  // o rascunho nunca venceria.
  if (idade < 0 || idade > DRAFT_TTL_MS) return descartar();

  const state = sanitizeDraftState(env.state);
  // Rascunho sem nada digitado não vira banner — oferecer "restaurar" um
  // formulário vazio só atrapalha.
  if (!isDraftDirty(state)) return descartar();

  return { state, savedAt };
}

/**
 * Carimbo do rascunho → "21/08/2026 às 09:05" (fuso do usuário).
 *
 * Formatação manual em vez de `toLocaleString`: o resultado precisa ser o mesmo
 * no navegador e no teste, sem depender do ICU do runtime.
 */
export function formatDraftTimestamp(ts) {
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} às ${p(d.getHours())}:${p(d.getMinutes())}`;
}
